import { useAuth } from '@clerk/react'
import { createContext, type ReactNode, use, useCallback, useMemo } from 'react'
import { trpc } from '@/lib/trpc'

// Types
interface ListInfo {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  sourceType: string | null
  sortBy: string
  sortDir: string
  problemCount: number
}

interface ProblemListContextValue {
  lists: ListInfo[]
  memberships: Record<string, string[]>
  isLoading: boolean
  isInAnyList: (problemId: string) => boolean
  getListsForProblem: (problemId: string) => string[]
  favoritesId: string | null
  toggleProblemInList: (listId: string, problemId: string) => void
  createList: (name: string) => Promise<{ id: string; name: string } | null>
  refetch: () => void
}

// Context + hooks
const ProblemListContext = createContext<ProblemListContextValue | null>(null)

export function useProblemLists() {
  const ctx = use(ProblemListContext)
  if (!ctx)
    throw new Error('useProblemLists must be used within a ProblemListProvider')
  return ctx
}

export function useProblemListsOptional() {
  return use(ProblemListContext)
}

// Provider
export function ProblemListProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth()
  const utils = trpc.useUtils()

  const { data, isLoading, refetch } = trpc.lists.getUserListData.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  const toggleMutation = trpc.lists.toggleProblemInList.useMutation({
    onMutate: async ({ listId, problemId }) => {
      await utils.lists.getUserListData.cancel()
      const previous = utils.lists.getUserListData.getData()

      if (previous) {
        const memberships = { ...previous.memberships }
        const existing = memberships[problemId] ?? []
        if (existing.includes(listId)) {
          memberships[problemId] = existing.filter((id) => id !== listId)
          if (memberships[problemId].length === 0) delete memberships[problemId]
        } else {
          memberships[problemId] = [...existing, listId]
        }
        utils.lists.getUserListData.setData(undefined, {
          ...previous,
          memberships,
        })
      }

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        utils.lists.getUserListData.setData(undefined, context.previous)
      }
    },
    onSettled: () => {
      utils.lists.getUserListData.invalidate()
      utils.lists.getListDetail.invalidate()
    },
  })

  const createMutation = trpc.lists.create.useMutation({
    onSuccess: () => {
      utils.lists.getUserListData.invalidate()
    },
  })

  const lists = useMemo(() => {
    if (!data?.lists) return []
    return [...data.lists].sort((a, b) => {
      if (a.isSystem && !b.isSystem) return -1
      if (!a.isSystem && b.isSystem) return 1
      return 0
    })
  }, [data?.lists])

  const memberships = useMemo(
    () => data?.memberships ?? {},
    [data?.memberships],
  )

  const favoritesId = useMemo(
    () => lists.find((l) => l.isSystem)?.id ?? null,
    [lists],
  )

  const isInAnyList = useCallback(
    (problemId: string) => (memberships[problemId]?.length ?? 0) > 0,
    [memberships],
  )

  const getListsForProblem = useCallback(
    (problemId: string) => memberships[problemId] ?? [],
    [memberships],
  )

  const toggleProblemInList = useCallback(
    (listId: string, problemId: string) => {
      toggleMutation.mutate({ listId, problemId })
    },
    [toggleMutation],
  )

  const createList = useCallback(
    async (name: string) => {
      try {
        const result = await createMutation.mutateAsync({ name })
        return result ? { id: result.id, name: result.name } : null
      } catch {
        return null
      }
    },
    [createMutation],
  )

  const value = useMemo<ProblemListContextValue>(
    () => ({
      lists,
      memberships,
      isLoading,
      isInAnyList,
      getListsForProblem,
      favoritesId,
      toggleProblemInList,
      createList,
      refetch,
    }),
    [
      lists,
      memberships,
      isLoading,
      isInAnyList,
      getListsForProblem,
      favoritesId,
      toggleProblemInList,
      createList,
      refetch,
    ],
  )

  return <ProblemListContext value={value}>{children}</ProblemListContext>
}
